const DEFAULT_FILE_NAME = 'reporte';

const normalizeFileName = (fileName) => {
  const value = String(fileName || DEFAULT_FILE_NAME).trim() || DEFAULT_FILE_NAME;
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase() || DEFAULT_FILE_NAME;
};

const stripHtml = (value) => {
  if (value == null) return '';
  const text = String(value);
  if (typeof document === 'undefined' || !/<[a-z][\s\S]*>/i.test(text)) {
    return text.replace(/\s+/g, ' ').trim();
  }

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = text;
  return (tempDiv.textContent || tempDiv.innerText || '').replace(/\s+/g, ' ').trim();
};

const escapeHtml = (value) => stripHtml(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const isPlainExportValue = (value) => {
  if (value == null || value instanceof Date) return true;
  return ['string', 'number', 'boolean'].includes(typeof value);
};

const formatDate = (value) => value.toLocaleString('es-AR', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit'
});

const resolveCellValue = (row, column) => {
  const rawValue = row?.[column.key];
  let value = rawValue;

  if (typeof column.exportFormatter === 'function') {
    value = column.exportFormatter(rawValue, row);
  } else if (typeof column.formatter === 'function') {
    const formattedValue = column.formatter(rawValue, row);
    value = isPlainExportValue(formattedValue) ? formattedValue : rawValue;
  }

  if (value == null) return '';
  if (value instanceof Date) return formatDate(value);
  if (typeof value === 'object') return stripHtml(JSON.stringify(value));
  return stripHtml(value);
};

const getExportableColumns = (columns) => columns.filter(column => column.exportable !== false && column.key !== 'actions');

const getColumnWeight = (column) => {
  const key = String(column.key || '').toLowerCase();
  const title = String(column.title || '').toLowerCase();

  if (key.includes('mensaje') || key.includes('respuesta') || title.includes('mensaje') || title.includes('respuesta')) {
    return 2.8;
  }

  if (key.includes('nombre') || title.includes('nombre')) {
    return 1.8;
  }

  if (key.includes('fecha') || key.includes('createdat') || title.includes('fecha')) {
    return 1.1;
  }

  if (key.includes('thread') || key.includes('conversation') || key.includes('id')) {
    return 1.25;
  }

  return 1.35;
};

const getPdfColumnStyles = (columns, availableWidth) => {
  const exportColumns = getExportableColumns(columns);
  const weights = exportColumns.map(getColumnWeight);
  const totalWeight = weights.reduce((sum, weight) => sum + weight, 0) || 1;

  return exportColumns.reduce((styles, column, index) => {
    const key = String(column.key || '').toLowerCase();
    styles[index] = {
      cellWidth: (availableWidth * weights[index]) / totalWeight,
      halign: column.align === 'center' || key.includes('fecha') || key.includes('createdat') ? 'center' : 'left'
    };
    return styles;
  }, {});
};

const buildHtmlTable = ({ rows, columns, title }) => {
  const exportColumns = getExportableColumns(columns);
  const header = exportColumns
    .map(column => `<th>${escapeHtml(column.exportTitle || column.title || column.key)}</th>`)
    .join('');

  const body = rows.map(row => {
    const cells = exportColumns
      .map(column => `<td>${escapeHtml(resolveCellValue(row, column))}</td>`)
      .join('');
    return `<tr>${cells}</tr>`;
  }).join('');

  return `
    <table>
      ${title ? `<caption>${escapeHtml(title)}</caption>` : ''}
      <thead><tr>${header}</tr></thead>
      <tbody>${body}</tbody>
    </table>
  `;
};

export const exportTableAsExcel = ({ rows = [], columns = [], fileName, title }) => {
  const html = `
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <style>
          table { border-collapse: collapse; width: 100%; }
          caption { font-size: 18px; font-weight: 700; margin: 0 0 12px; text-align: left; }
          th, td { border: 1px solid #d1d5db; padding: 8px; vertical-align: top; }
          th { background: #f3f4f6; font-weight: 700; }
        </style>
      </head>
      <body>${buildHtmlTable({ rows, columns, title })}</body>
    </html>
  `;

  const blob = new Blob(['\ufeff', html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${normalizeFileName(fileName)}.xls`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const exportTableAsPdf = async ({ rows = [], columns = [], fileName, title }) => {
  const [{ jsPDF }, autoTableModule] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable')
  ]);
  const autoTable = autoTableModule.default;
  const exportColumns = getExportableColumns(columns);
  const normalizedTitle = title || fileName || DEFAULT_FILE_NAME;
  const documentFileName = normalizeFileName(fileName);
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: 'a4'
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = { top: 74, right: 28, bottom: 42, left: 28 };
  const availableWidth = pageWidth - margin.left - margin.right;
  const generatedAt = new Date().toLocaleString('es-AR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });

  doc.setProperties({
    title: normalizedTitle,
    subject: 'Reporte',
    creator: 'UTN OpenAI'
  });

  autoTable(doc, {
    head: [exportColumns.map(column => column.exportTitle || column.title || column.key)],
    body: rows.map(row => exportColumns.map(column => resolveCellValue(row, column))),
    startY: margin.top,
    margin,
    theme: 'grid',
    tableWidth: availableWidth,
    pageBreak: 'auto',
    rowPageBreak: 'auto',
    styles: {
      font: 'helvetica',
      fontSize: 7,
      cellPadding: 4,
      overflow: 'linebreak',
      valign: 'top',
      lineColor: [209, 213, 219],
      lineWidth: 0.4,
      textColor: [17, 24, 39]
    },
    headStyles: {
      fillColor: [31, 41, 55],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      halign: 'center',
      valign: 'middle'
    },
    alternateRowStyles: {
      fillColor: [249, 250, 251]
    },
    columnStyles: getPdfColumnStyles(columns, availableWidth),
    didDrawPage: () => {
      const currentPage = doc.internal.getNumberOfPages();

      doc.setFillColor(248, 250, 252);
      doc.rect(0, 0, pageWidth, 56, 'F');
      doc.setTextColor(17, 24, 39);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.text(normalizedTitle, margin.left, 25, {
        maxWidth: availableWidth * 0.72
      });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(75, 85, 99);
      doc.text(`Generado: ${generatedAt}`, margin.left, 42);
      doc.text(`${rows.length} registros`, pageWidth - margin.right, 42, { align: 'right' });

      doc.setDrawColor(229, 231, 235);
      doc.line(margin.left, 56, pageWidth - margin.right, 56);

      doc.setFontSize(8);
      doc.setTextColor(107, 114, 128);
      doc.text(`Página ${currentPage}`, pageWidth - margin.right, pageHeight - 18, { align: 'right' });
    }
  });

  doc.save(`${documentFileName}.pdf`);
  return true;
};
